/* eslint-disable react-hooks/exhaustive-deps */
import { Tooltip } from "@material-ui/core";
import TouchAppIcon from "@material-ui/icons/TouchApp";
import { PENDING_PAYMENT } from "app/appConfig";
import GlobitsPagingAutocomplete from "app/common/form/GlobitsPagingAutocomplete";
import GlobitsRadioButton from "app/common/form/GlobitsRadioButton";
import GlobitsVNDCurrencyInput from "app/common/form/GlobitsVNDCurrencyInput";
import {
  PAYMENT_METHOD_CHARACTERISTIC_CASH_CODE,
  PAYMENT_METHOD_CHARACTERISTIC_DEDUCT_FROM_DEPOSIT,
  PAYMENT_METHOD_CHARACTERISTIC_HOME_CREDIT,
  PAYMENT_METHOD_CHARACTERISTIC_HOME_CREDIT_CODE,
  PAYMENT_METHOD_CHARACTERISTIC_PENDING_PAYMENT_CODE,
  PAYMENT_METHOD_CHARACTERISTIC_POS,
  PAYMENT_METHOD_CHARACTERISTIC_POS_VNPAY_TRAGOP,
  PAYMENT_METHOD_CHARACTERISTIC_REQUIRES_BANK_ACCOUNT,
  PAYMENT_METHOD_CHARACTERISTIC_USE_PREPAID_CARD,
  TYPE_GLOBAL_PROPOTIES_BANK,
  TYPE_GLOBAL_PROPOTIES_HOME_CREDIT_CODE,
  TYPE_GLOBAL_PROPOTIES_POS_LIST,
  TYPE_GLOBAL_PROPOTIES_POS_VNPAY_TRAGOP,
} from "app/common/LocalConst";
import {} from "app/common/LocalFunction";
import { pagingExtGlobalProperty } from "app/services/GlobalPropertyService";
import { useStore } from "app/stores";
import { getIn, useFormikContext } from "formik";
import { observer } from "mobx-react";
import React, { memo, useEffect } from "react";
import SelectCardFeePopup from "../Popup/SelectCardFeePopup";
import SelectDepositMoneyFeePopup from "../Popup/SelectDepositMoneyFeePopup";

/**
 * TotalOfVoucherTypeRefund: Thành phần xử lý hình thức thanh toán chuyên biệt cho màn hình Hoàn Tiền Cọc.
 * 
 * Khác biệt chính so với TotalOfVoucherTypeCash:
 * 1. Hỗ trợ Namespace: Cho phép quản lý nhiều danh sách hình thức thanh toán lồng nhau (vídụ: items[0].voucherPaymentMethods).
 * 2. Cân đối hai chiều (Bidirectional Balancing): Tự động điều chỉnh Tiền mặt (hoặc HTTT khác) khi người dùng sửa bất kỳ ô nào 
 *    để tổng HTTT luôn bằng 'Số tiền hoàn'.
 * 3. Đồng bộ ngược (Conditional Sync): 
 *    - Mode không gộp: Khi sửa tiền ở bảng HTTT, cập nhật ngược lại 'Số tiền hoàn' của dòng tương ứng.
 *    - Mode gộp: Vô hiệu hóa tính năng này để 'Số tiền hoàn' ở các dòng là nguồn dữ liệu gốc (source of truth).
 * 4. Độc lập: Tách riêng để tùy chỉnh logic mà không ảnh hưởng đến các màn hình Thu/Chi/Tạm ứng/Khám bệnh.
 * 
 * Giống với TotalOfVoucherTypeCash:
 * 1. Giao diện (UI): Sử dụng chung bảng hiển thị, icon, định dạng tiền tệ và bộ chọn Radio button.
 * 2. Logic HTTT: Chia sẻ các logic lọc hình thức thanh toán, tự động nạp STK/Máy POS theo cơ sở.
 */
function TotalOfVoucherTypeRefund({
  nameSpace,
  disabled,
  moneyAfterDiscount,
  listPaymentMethod,
  listStk,
  patientId,
  listPos,
  listHomecredit,
  listPos_tragop,
}) {
  const { values, setFieldValue, errors } = useFormikContext();

  const withNameSpace = (name, index) => {
    return `${nameSpace}[${index}].${name}`;
  };

  useEffect(() => {
    const methods = getIn(values, nameSpace) || [];
    let sum = (methods || []).reduce((acc, el) => {
        if (el?.paymentMethodValue?.code === PAYMENT_METHOD_CHARACTERISTIC_CASH_CODE) {
            return acc;
        }
        return acc + (+el?.totalOfMethod || 0);
    }, 0);

    let updatedMethods = methods.map((el) => {
      if (
        el?.paymentMethodValue?.code === PAYMENT_METHOD_CHARACTERISTIC_CASH_CODE
      ) {
        return {
          ...el,
          totalOfMethod: +moneyAfterDiscount - sum,
          bankName: el?.paymentBank?.propertyName,
          bankAccountNumber: el?.paymentBank?.property,
        };
      }
      return el;
    });

    setFieldValue(nameSpace, updatedMethods);
  }, [moneyAfterDiscount, nameSpace]);

  const handleChangeTotalOfMethod = (e, item, index) => {
    let value = e?.target?.value;

    //set value
    setFieldValue(withNameSpace("totalOfMethod", index), value);
    
    const methods = getIn(values, nameSpace) || [];
    let newVoucherPaymentMethods =
      methods.length > 0 &&
      methods.map((el, ind) => {
        if (index === ind) return { ...el, totalOfMethod: value };
        else return el;
      });

    if (nameSpace === "voucherPaymentMethods") {
        const cashIndex = newVoucherPaymentMethods.findIndex(el => el?.paymentMethodValue?.code === PAYMENT_METHOD_CHARACTERISTIC_CASH_CODE);
        
        // Xác định ô "đối ứng" để bù trừ tiền (Ưu tiên bù trừ vào Tiền mặt nếu đang sửa mục khác)
        let targetIndex = -1;
        if (index !== cashIndex && cashIndex !== -1) {
            targetIndex = cashIndex;
        } else {
            targetIndex = newVoucherPaymentMethods.findIndex((el, ind) => ind !== index);
        }

        if (targetIndex !== -1) {
            // Tính tổng các ô còn lại trừ ô đang sửa và ô đối ứng
            const otherSumWithoutTarget = newVoucherPaymentMethods.reduce((acc, el, ind) => {
                if (ind === index || ind === targetIndex) return acc;
                return acc + (+el.totalOfMethod || 0);
            }, 0);

            // Tính giá trị mới của ô đối ứng = Tổng - Các ô khác - Giá trị đang nhập
            let newTargetVal = moneyAfterDiscount - otherSumWithoutTarget - (+value || 0);
            
            if (newTargetVal < 0) {
                // Nếu giá trị đối ứng bị âm -> chứng tỏ giá trị đang nhập quá lớn. 
                // Cần giới hạn lại giá trị đang nhập để ô đối ứng tối thiểu là 0
                newTargetVal = 0;
                value = moneyAfterDiscount - otherSumWithoutTarget;
                setFieldValue(withNameSpace("totalOfMethod", index), value);
            }
            
            setFieldValue(withNameSpace("totalOfMethod", targetIndex), newTargetVal);
        }
    }
      
    //if method bank then set stk account bank
    if (
      item?.characteristic ===
      PAYMENT_METHOD_CHARACTERISTIC_REQUIRES_BANK_ACCOUNT
    ) {
      setFieldValue(withNameSpace("paymentBank", index), listStk?.[0]);
    }

    if (item?.characteristic === PAYMENT_METHOD_CHARACTERISTIC_POS) {
      setFieldValue(withNameSpace("paymentBank", index), listPos?.[0]);
    }

    if (
      item?.characteristic === PAYMENT_METHOD_CHARACTERISTIC_POS_VNPAY_TRAGOP
    ) {
      setFieldValue(withNameSpace("paymentBank", index), listPos_tragop?.[0]);
    }

    if (item?.characteristic === PAYMENT_METHOD_CHARACTERISTIC_HOME_CREDIT) {
      setFieldValue(withNameSpace("paymentBank", index), listHomecredit?.[0]);
    }

    // Tính tổng số tiền từ tất cả các hình thức thanh toán
    const totalSum = newVoucherPaymentMethods.reduce((acc, el) => acc + (+el.totalOfMethod || 0), 0);

    // Cập nhật ngược lại refundAmount và phân bổ doanh thu ở dòng cha nếu tổng số tiền thay đổi
    // Chỉ thực hiện ở mode Không Gộp (nameSpace !== "voucherPaymentMethods")
    const basePath = nameSpace.replace(".voucherPaymentMethods", "");
    if (nameSpace !== "voucherPaymentMethods" && totalSum !== moneyAfterDiscount) {
      setFieldValue(`${basePath}.refundAmount`, totalSum);

      const staffRevenues = getIn(values, `${basePath}.staffRevenues`) || [];
      const updatedSR = staffRevenues.map((sr) => ({
        ...sr,
        actualAmount: (totalSum * (sr.percentRevenue || 0)) / 100,
      }));
      setFieldValue(`${basePath}.staffRevenues`, updatedSR);
    }
  };
  
  return (
    <div
      style={{ overflow: "auto", scrollbarWidth: "thin", padding: "0" }}
      className="table-root table-concept-datatype"
    >
      <div style={{ maxHeight: "60vh", padding: "0" }}>
        <table className="table-container table-layout-fixed">
          <thead className="row-table-header">
            <tr>
              <th width="60%" style={{ color: "#4276a4" }}>
                HTTT
              </th>
              <th width="40%" style={{ color: "#4276a4" }}>
                Số tiền
              </th>
            </tr>
          </thead>
          <tbody>
            {listPaymentMethod?.length > 0 &&
              listPaymentMethod?.map((el, index) => {
                const characteristic = el?.characteristic;
                const code = el?.code;
                if (
                  el?.isUsing &&
                  (code === PAYMENT_METHOD_CHARACTERISTIC_CASH_CODE ||
                    code === PAYMENT_METHOD_CHARACTERISTIC_HOME_CREDIT_CODE ||
                    (PENDING_PAYMENT && code === PAYMENT_METHOD_CHARACTERISTIC_PENDING_PAYMENT_CODE) ||
                    characteristic === PAYMENT_METHOD_CHARACTERISTIC_REQUIRES_BANK_ACCOUNT ||
                    characteristic === PAYMENT_METHOD_CHARACTERISTIC_POS ||
                    characteristic === PAYMENT_METHOD_CHARACTERISTIC_POS_VNPAY_TRAGOP ||
                    characteristic === PAYMENT_METHOD_CHARACTERISTIC_HOME_CREDIT)
                ) {
                  return (
                    <React.Fragment key={index}>
                      <tr key={index}>
                        <td>
                          <div className="flex align-center">
                            <GlobitsRadioButton
                              name={withNameSpace("totalRadio", index)}
                              value={el + 1}
                              type="radio"
                              checked={getIn(values, withNameSpace("totalOfMethod", index)) == moneyAfterDiscount}
                              onChange={(e) => {
                                if (characteristic === PAYMENT_METHOD_CHARACTERISTIC_USE_PREPAID_CARD) {
                                  return;
                                }
                                const currentMethods = getIn(values, nameSpace) || [];
                                let updatedMethods = currentMethods.map((elpm, stt) => {
                                  if (stt === index) {
                                    return {
                                      ...elpm,
                                      totalOfMethod: +moneyAfterDiscount,
                                    };
                                  } else {
                                    return {
                                      ...elpm,
                                      totalOfMethod: 0,
                                    };
                                  }
                                });
                                setFieldValue(nameSpace, updatedMethods);

                                if (characteristic == PAYMENT_METHOD_CHARACTERISTIC_REQUIRES_BANK_ACCOUNT) {
                                  setFieldValue(withNameSpace("paymentBank", index), listStk?.[0]);
                                }

                                if (characteristic == PAYMENT_METHOD_CHARACTERISTIC_POS) {
                                  setFieldValue(withNameSpace("paymentBank", index), listPos?.[0]);
                                }

                                if (characteristic == PAYMENT_METHOD_CHARACTERISTIC_HOME_CREDIT) {
                                  setFieldValue(withNameSpace("paymentBank", index), listHomecredit?.[0]);
                                }
                                if (characteristic == PAYMENT_METHOD_CHARACTERISTIC_POS_VNPAY_TRAGOP) {
                                  setFieldValue(withNameSpace("paymentBank", index), listPos_tragop?.[0]);
                                }
                              }}
                            />
                            {el?.name}
                          </div>
                        </td>

                        <td>
                          <GlobitsVNDCurrencyInput
                            name={withNameSpace("totalOfMethod", index)}
                            disabled={
                              disabled ||
                              characteristic === PAYMENT_METHOD_CHARACTERISTIC_USE_PREPAID_CARD ||
                              characteristic === PAYMENT_METHOD_CHARACTERISTIC_DEDUCT_FROM_DEPOSIT
                            }
                            onChange={(e) => handleChangeTotalOfMethod(e, el, index)}
                          />
                        </td>
                      </tr>

                      {characteristic === PAYMENT_METHOD_CHARACTERISTIC_REQUIRES_BANK_ACCOUNT &&
                        getIn(values, withNameSpace("totalOfMethod", index)) != 0 && (
                          <tr>
                            <td colSpan={2}>
                              <GlobitsPagingAutocomplete
                                disabled={disabled}
                                placeholder="Chọn stk"
                                name={withNameSpace("paymentBank", index)}
                                error={!!getIn(errors, withNameSpace("paymentBank", index))}
                                helperText={getIn(errors, withNameSpace("paymentBank", index))}
                                api={pagingExtGlobalProperty}
                                searchObject={{
                                  isActive: true,
                                  dataType: TYPE_GLOBAL_PROPOTIES_BANK,
                                  departmentIds: values?.department ? [values?.department.id] : [],
                                }}
                                getOptionLabel={(option) => `${option?.propertyName} - ${option?.property}`}
                                onChange={(_, value) => {
                                  setFieldValue(withNameSpace("paymentBank", index), value);
                                  setFieldValue(withNameSpace("bankAccountNumber", index), value?.property);
                                  setFieldValue(withNameSpace("bankName", index), value?.propertyName);
                                }}
                              />
                            </td>
                          </tr>
                        )}

                      {characteristic === PAYMENT_METHOD_CHARACTERISTIC_POS &&
                        getIn(values, withNameSpace("totalOfMethod", index)) != 0 && (
                          <tr>
                            <td colSpan={2}>
                              <GlobitsPagingAutocomplete
                                disabled={disabled}
                                placeholder="Chọn máy pos"
                                name={withNameSpace("paymentBank", index)}
                                error={!!getIn(errors, withNameSpace("paymentBank", index))}
                                helperText={getIn(errors, withNameSpace("paymentBank", index))}
                                api={pagingExtGlobalProperty}
                                searchObject={{
                                  isActive: true,
                                  dataType: TYPE_GLOBAL_PROPOTIES_POS_LIST,
                                  departmentIds: values?.department ? [values?.department.id] : [],
                                }}
                                getOptionLabel={(option) => `${option?.propertyName} - ${option?.property}`}
                                onChange={(_, value) => {
                                  setFieldValue(withNameSpace("paymentBank", index), value);
                                  setFieldValue(withNameSpace("bankAccountNumber", index), value?.property);
                                  setFieldValue(withNameSpace("bankName", index), value?.propertyName);
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      {characteristic === PAYMENT_METHOD_CHARACTERISTIC_HOME_CREDIT &&
                        getIn(values, withNameSpace("totalOfMethod", index)) != 0 && (
                          <tr>
                            <td colSpan={2}>
                              <GlobitsPagingAutocomplete
                                disabled={disabled}
                                placeholder="Chọn home credit"
                                name={withNameSpace("paymentBank", index)}
                                error={!!getIn(errors, withNameSpace("paymentBank", index))}
                                helperText={getIn(errors, withNameSpace("paymentBank", index))}
                                api={pagingExtGlobalProperty}
                                searchObject={{
                                  isActive: true,
                                  dataType: TYPE_GLOBAL_PROPOTIES_HOME_CREDIT_CODE,
                                  departmentIds: values?.department ? [values?.department.id] : [],
                                }}
                                getOptionLabel={(option) => `${option?.propertyName} - ${option?.property}`}
                                onChange={(_, value) => {
                                  setFieldValue(withNameSpace("paymentBank", index), value);
                                  setFieldValue(withNameSpace("bankAccountNumber", index), value?.property);
                                  setFieldValue(withNameSpace("bankName", index), value?.propertyName);
                                }}
                              />
                            </td>
                          </tr>
                        )}

                      {characteristic === PAYMENT_METHOD_CHARACTERISTIC_POS_VNPAY_TRAGOP &&
                        getIn(values, withNameSpace("totalOfMethod", index)) != 0 && (
                          <tr>
                            <td colSpan={2}>
                              <GlobitsPagingAutocomplete
                                disabled={disabled}
                                placeholder="Chọn máy pos vnpay trả góp"
                                name={withNameSpace("paymentBank", index)}
                                error={!!getIn(errors, withNameSpace("paymentBank", index))}
                                helperText={getIn(errors, withNameSpace("paymentBank", index))}
                                api={pagingExtGlobalProperty}
                                searchObject={{
                                  isActive: true,
                                  dataType: TYPE_GLOBAL_PROPOTIES_POS_VNPAY_TRAGOP,
                                  departmentIds: values?.department ? [values?.department.id] : [],
                                }}
                                getOptionLabel={(option) => `${option?.propertyName} - ${option?.property}`}
                                onChange={(_, value) => {
                                  setFieldValue(withNameSpace("paymentBank", index), value);
                                  setFieldValue(withNameSpace("bankAccountNumber", index), value?.property);
                                  setFieldValue(withNameSpace("bankName", index), value?.propertyName);
                                }}
                              />
                            </td>
                          </tr>
                        )}
                    </React.Fragment>
                  );
                }
                return <React.Fragment key={index}></React.Fragment>;
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default memo(observer(TotalOfVoucherTypeRefund));
